import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { clearSelectedCompanyKey } from '@/lib/companySelectionStorage';
import { clearAllCompanyDashboardFilters } from '@/features/company/companyDashboardFiltersStorage';
import { clearImpersonationSession } from '@/features/system/impersonationStorage';
// ========== impersonate capability (remove this import + useEffect below when done) ==========
import { getApiBaseUrl } from '@/lib/api/client';
import { IMPERSONATE, EMAIL_IMPERSONATE } from '@/lib/auth/impersonation.config';
// ========== end impersonate capability ==========

interface AuthContextValue {
  user: FirebaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const impersonateEnabled = IMPERSONATE && EMAIL_IMPERSONATE.trim().length > 0;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      // Keep loading until impersonate finishes (pnpm prod uses production mode, not import.meta.env.DEV).
      if (!impersonateEnabled || firebaseUser) {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [impersonateEnabled]);

  // ========== impersonate capability (remove this useEffect when done) ==========
  useEffect(() => {
    if (!impersonateEnabled) return;
    let cancelled = false;
    const baseUrl = getApiBaseUrl();
    fetch(`${baseUrl}/api/dev/impersonate-token?email=${encodeURIComponent(EMAIL_IMPERSONATE.trim())}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error((body as { error?: string }).error ?? `HTTP ${r.status}`);
        }
        const token = (body as { token?: string }).token;
        if (!token) throw new Error('No token in response');
        if (!cancelled) await signInWithCustomToken(auth, token);
      })
      .catch((e) => {
        console.error('[impersonate] Failed to sign in as', EMAIL_IMPERSONATE, '—', e?.message ?? e,
          '\nEnsure Functions emulator is running (`npx firebase emulators:start --only functions --project prod`), ALLOW_IMPERSONATE=true in apps/api/.env, and IMPERSONATE_SERVICE_ACCOUNT_PATH points at prod service account JSON.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [impersonateEnabled]);
  // ========== end impersonate capability ==========

  const signIn = async (email: string, password: string) => {
    // Fresh sign-in: drop persisted company so multi-company users hit /choose-company
    // instead of skipping it when localStorage still holds a valid key from an older session.
    clearSelectedCompanyKey();
    clearAllCompanyDashboardFilters();
    clearImpersonationSession();
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signOut = async () => {
    clearSelectedCompanyKey();
    clearAllCompanyDashboardFilters();
    clearImpersonationSession();
    await firebaseSignOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/sign-in`,
      handleCodeInApp: false,
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
